// Auth modal. Plan 24:
//   - Modal + Button + FormInput + FormSelect + FormCheckbox + ErrorAlert.
//   - No more bespoke backdrop / close-button / input styling in this
//     file; all of it lives in `src/shared/ui`.
// Plan 23 kept react-hook-form in the driver's seat for the fields.

import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { extractErrorCode, userMessageForError } from '../../shared/errors/messageFor';
import {
  Button,
  ErrorAlert,
  FormCheckbox,
  FormInput,
  FormSelect,
  Heading,
  LocationAutocomplete,
  Modal,
} from '../../shared/ui';
import { requestPasswordReset, sendVerification } from './authApi';
import { trackPasswordResetRequest } from '../../shared/analytics/track';
import { useAuth } from './AuthProvider';

type AuthMode = 'login' | 'register' | 'forgot';

interface AuthFormFields {
  email: string;
  password: string;
  name: string;
  telegram: string;
  birth_date: string;
  birth_time: string;
  birth_location: string;
  birth_time_type: string;
  verification_code: string;
  /** Plan 74 — registration-time marketing-email opt-in. Default unchecked. */
  marketing_consent: boolean;
}

const BIRTH_TIME_OPTIONS = [
  {
    value: 'confirmed',
    label:
      'I can confirm this birth time/minute is accurate and directly from a reliable source (ie, Birth Certificate, rectification, etc..)',
  },
  {
    value: 'approximate',
    label:
      'This birth time is an estimate (ie, relatives’ memory, etc…) I want to proceed with the reading using this birth time.',
  },
  {
    value: 'unknown',
    label: 'Birth Time UNKNOWN. I want to proceed with the reading without a birth time.',
  },
];

export const AuthModal = () => {
  const { isAuthModalOpen, closeAuthModal, login, register: registerUser } = useAuth();
  const [mode, setMode] = useState<AuthMode>('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // Guided help for a non-technical audience (owner ask, 2026-07-10):
  // when sign-in or forgot-password hits an email with no account, show a
  // warm walkthrough (check the spelling / you may have used another
  // address / create an account) instead of a bare error. Holds the email
  // that missed so the register hand-off can carry it over.
  const [noAccountEmail, setNoAccountEmail] = useState<string | null>(null);

  const isLogin = mode === 'login';
  const isRegister = mode === 'register';
  const isForgot = mode === 'forgot';

  const { register, handleSubmit, watch, setValue, reset } = useForm<AuthFormFields>({
    defaultValues: {
      email: '',
      password: '',
      name: '',
      telegram: '',
      birth_date: '',
      birth_time: '',
      birth_location: '',
      birth_time_type: 'confirmed',
      verification_code: '',
      // Plan 74: opt-in must be opt-in, not opt-out. Default unchecked.
      marketing_consent: false,
    },
  });

  const emailValue = watch('email');
  const birthLocationValue = watch('birth_location');

  // The submit button sits at the bottom of a tall form; on a wrong-code or
  // missing-field error the user is anchored at the bottom and would never
  // see the banner that just appeared at the top. Scroll the alert itself
  // into view so they actually find out what went wrong.
  const errorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Optional call: jsdom doesn't implement scrollIntoView.
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  // Same anchoring for the guided no-account panel, and dismiss it as soon
  // as the user starts correcting the email it refers to.
  const noAccountRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (noAccountEmail && noAccountRef.current) {
      noAccountRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [noAccountEmail]);
  useEffect(() => {
    if (noAccountEmail && emailValue !== noAccountEmail) {
      setNoAccountEmail(null);
    }
  }, [emailValue, noAccountEmail]);

  const verifyMutation = useMutation({
    mutationFn: (email: string) => sendVerification(email),
  });

  // Inline success/error feedback for the Verify / Resend button. Lives
  // beside the email field rather than the modal-level `error` state so
  // it doesn't compete with form-submit errors and stays contextually
  // attached to the action it describes. Replaces a former `alert(...)`
  // popup that also leaked dev-mode wording about server logs.
  const [verificationNotice, setVerificationNotice] = useState<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);

  const handleSendVerification = async () => {
    if (!emailValue) {
      setVerificationNotice({ kind: 'error', text: 'Please enter your email first.' });
      return;
    }
    setVerificationNotice(null);
    // Requesting a new code implicitly acknowledges any prior failed
    // submit (e.g. wrong-code error). Clear the modal-level error so
    // the user doesn't simultaneously see a red "didn't match" banner
    // at the top and the new green "code sent" notice below the email.
    setError('');
    try {
      await verifyMutation.mutateAsync(emailValue);
      setVerificationSent(true);
      setVerificationNotice({
        kind: 'success',
        text: `Verification code sent to ${emailValue}. Check your inbox (and spam folder, just in case).`,
      });
    } catch (err) {
      setVerificationNotice({
        kind: 'error',
        text: userMessageForError(err, 'Could not send verification code.'),
      });
    }
  };

  const onSubmit = async (fields: AuthFormFields) => {
    if (isRegister) {
      if (!disclaimerAccepted) {
        setError('Please accept the Birth Data Accuracy Disclaimer.');
        return;
      }
      if (!verificationSent) {
        setError('Please verify your email address first.');
        return;
      }
      if (!fields.verification_code) {
        setError('Please enter the verification code.');
        return;
      }
    }
    setError('');
    setNoAccountEmail(null);
    setLoading(true);
    try {
      if (isLogin) {
        await login({ email: fields.email, password: fields.password });
      } else if (isForgot) {
        const result = await requestPasswordReset(fields.email);
        // Track intent only (no email/PII on the event).
        trackPasswordResetRequest();
        if (result.accountFound === false) {
          // Owner decision 2026-07-10: guide the user instead of the old
          // anti-enumeration "if an account exists…" copy that left people
          // waiting for an email that would never arrive.
          setNoAccountEmail(fields.email);
        } else {
          setResetSent(true);
        }
      } else {
        await registerUser({
          email: fields.email,
          password: fields.password,
          name: fields.name,
          telegram: fields.telegram || undefined,
          birth_date: fields.birth_date || undefined,
          birth_time: fields.birth_time || undefined,
          birth_time_type: fields.birth_time_type,
          birth_location: fields.birth_location || undefined,
          verification_code: fields.verification_code,
          // Plan 74: only forward when explicitly true so the server's
          // schema-default (false) handles the opt-out path.
          marketing_consent: fields.marketing_consent === true ? true : undefined,
        });
      }
    } catch (err) {
      // Sign-in against an email with no account gets the guided panel,
      // not a bare error banner (owner ask, 2026-07-10).
      if (isLogin && extractErrorCode(err) === 'account_not_found') {
        setNoAccountEmail(fields.email);
        return;
      }
      const fallback = isLogin
        ? 'Sign in failed.'
        : isForgot
          ? 'Could not send the reset link.'
          : 'Registration failed.';
      // userMessageForError handles network/5xx/validation/code-mapped
      // resolution in one place; the fallback is the domain copy used only
      // when none of the structured paths match.
      setError(userMessageForError(err, fallback));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setDisclaimerAccepted(false);
    setVerificationSent(false);
    setResetSent(false);
    setError('');
    setVerificationNotice(null);
    setNoAccountEmail(null);
    reset();
  };

  /**
   * The guided panel's "Create an account" hand-off: switch to register
   * with the missed email carried over, so the user doesn't retype it
   * (and can still see it to catch a typo).
   */
  const switchToRegisterWithEmail = (email: string) => {
    switchMode('register');
    setValue('email', email);
  };

  const modalTitle = isForgot
    ? 'Reset Your Password'
    : isLogin
      ? 'Existing Account'
      : 'Create Account';

  return (
    <Modal isOpen={isAuthModalOpen} onClose={closeAuthModal} title={modalTitle} size="md">
      {error && (
        <div ref={errorRef}>
          <ErrorAlert message={error} className="mb-6" />
        </div>
      )}

      {/* Guided help when sign-in / forgot-password hits an email with no
          account. Warm and concrete for a non-technical audience: name the
          likely causes, offer the one-tap fix, keep the email they typed. */}
      {noAccountEmail && (
        <div
          ref={noAccountRef}
          role="status"
          data-testid="no-account-help"
          className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-3"
        >
          <p className="text-sm font-bold text-stone-900">
            We couldn&apos;t find an account for{' '}
            <span className="break-all underline decoration-amber-400 underline-offset-2">
              {noAccountEmail}
            </span>
          </p>
          <p className="text-sm text-stone-700 leading-relaxed">
            No worries — this usually means one of two things:
          </p>
          <ul className="text-sm text-stone-700 leading-relaxed list-disc pl-5 space-y-1">
            <li>
              A small typo — double-check the spelling above, or try another email address you might
              have used.
            </li>
            <li>You haven&apos;t created an account here yet.</li>
          </ul>
          <Button
            type="button"
            fullWidth
            onClick={() => switchToRegisterWithEmail(noAccountEmail)}
            aria-label="Create a new account with this email"
          >
            New here? Create your account
          </Button>
          <p className="text-xs text-stone-500 text-center">
            We&apos;ll carry this email over for you — nothing to retype.
          </p>
        </div>
      )}

      {isForgot && resetSent ? (
        <div className="space-y-6">
          <p className="text-sm text-stone-600 leading-relaxed">
            Your reset link is on its way to <strong>{emailValue}</strong> — it expires in 30
            minutes. Please check your inbox (and the spam folder, just in case).
          </p>
          <Button type="button" fullWidth size="lg" onClick={() => switchMode('login')}>
            Back to Sign In
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {isRegister && (
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                label="Full Name"
                type="text"
                density="compact"
                placeholder="John Doe"
                autoComplete="name"
                {...register('name', { required: true })}
              />
              <FormInput
                label="Telegram (Optional)"
                type="text"
                density="compact"
                placeholder="@username"
                autoComplete="off"
                {...register('telegram')}
              />
            </div>
          )}

          <div className="space-y-1">
            <label
              htmlFor="authEmail"
              className="text-xs font-bold uppercase text-stone-400 ml-1 tracking-wider block"
            >
              Email Address
            </label>
            <div className="flex gap-2">
              <FormInput
                id="authEmail"
                type="email"
                density="compact"
                placeholder="you@example.com"
                containerClassName="flex-1"
                autoComplete="email"
                {...register('email', { required: true })}
              />
              {isRegister && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  // Re-clickable after the first send so the user can request
                  // a fresh code if the first one didn't arrive (Resend
                  // delivery hiccup, typed in spam, etc.) or expired. Disabled
                  // only while the request is actually in flight.
                  disabled={verifyMutation.isPending}
                  onClick={handleSendVerification}
                >
                  {verifyMutation.isPending ? '...' : verificationSent ? 'Resend' : 'Verify'}
                </Button>
              )}
            </div>
            {/*
              Inline success/error feedback for the Verify / Resend
              button. Replaces a former `alert(...)` popup that also
              leaked dev-mode wording about server logs. Sits under the
              email row so it's visibly attached to the action that
              produced it.
            */}
            {isRegister && verificationNotice && (
              <p
                role="status"
                aria-live="polite"
                className={
                  verificationNotice.kind === 'success'
                    ? 'text-xs px-3 py-2 mt-2 rounded-lg bg-green-50 border border-green-100 text-green-700'
                    : 'text-xs px-3 py-2 mt-2 rounded-lg bg-red-50 border border-red-100 text-red-600'
                }
              >
                {verificationNotice.text}
              </p>
            )}
          </div>

          {isRegister && (
            <FormInput
              label={
                <>
                  Verification Code
                  {import.meta.env.DEV && (
                    <span
                      className="ml-2 text-amber-600 font-normal normal-case"
                      data-testid="dev-verification-hint"
                      aria-label="Development-only hint"
                    >
                      Dev only: use 123456
                    </span>
                  )}
                </>
              }
              type="text"
              density="compact"
              placeholder="6-digit code"
              autoComplete="one-time-code"
              inputMode="numeric"
              {...register('verification_code', { required: isRegister })}
            />
          )}

          {/*
          Plan 74 — registration-time marketing-email consent.
          Sits directly below the email field (and verification code in
          register mode) and BEFORE the password input per the spec. Default
          unchecked. Copy is verbatim from plan 74's spec.
        */}
          {isRegister && (
            <FormCheckbox
              id="marketingConsent"
              data-testid="marketing-consent-checkbox"
              label={
                <span className="text-xs text-stone-600 leading-relaxed">
                  Yes, I'd like to receive occasional newsletter, promotions, and special offers by
                  email. I can unsubscribe at any time.
                </span>
              }
              {...register('marketing_consent')}
            />
          )}

          {!isForgot && (
            <div className="space-y-1">
              <FormInput
                label="Password"
                type="password"
                density="compact"
                placeholder="••••••••"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                {...register('password', { required: !isForgot })}
              />
              {isLogin && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-xs text-stone-500 hover:text-stone-900 underline tracking-wide"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>
          )}

          {isRegister && (
            <>
              <div className="p-4 bg-stone-200/50 rounded-2xl border border-stone-300/50">
                <FormCheckbox
                  id="disclaimer"
                  required
                  checked={disclaimerAccepted}
                  onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                  label={
                    <span className="text-sm font-bold text-stone-900">
                      Birth Data Accuracy Disclaimer
                    </span>
                  }
                  description="I understand that Human Design requires exact birth data from an official birth certificate or rectification specialist. I acknowledge that the reader cannot be held responsible for inaccuracies resulting from incorrect birth data provided."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Birth Date"
                  type="date"
                  density="compact"
                  // `bday` is in the HTML spec but not on Chrome's
                  // recognized-autofill token list, so DevTools flags it
                  // as "non-standard". Browsers basically never autofill
                  // a birthday anyway — explicit `off` silences the
                  // warning and keeps the date-picker UX intact.
                  autoComplete="off"
                  {...register('birth_date', { required: isRegister })}
                />
                <FormInput
                  label="Exact Time"
                  type="time"
                  density="compact"
                  autoComplete="off"
                  {...register('birth_time', { required: isRegister })}
                />
              </div>

              <FormSelect
                label="Birth Time Accuracy"
                density="compact"
                options={BIRTH_TIME_OPTIONS}
                {...register('birth_time_type', { required: isRegister })}
              />

              <LocationAutocomplete
                id="authLocation"
                label="Birth Location"
                density="compact"
                placeholder="City, State, Country"
                allowUnknown
                value={birthLocationValue}
                onChange={(next) => setValue('birth_location', next)}
              />
            </>
          )}

          <Button type="submit" fullWidth size="lg" loading={loading} disabled={loading}>
            {loading
              ? 'Processing...'
              : isLogin
                ? 'Sign In'
                : isForgot
                  ? 'Send Reset Link'
                  : 'Create Account'}
          </Button>

          {isLogin ? (
            <div className="text-left mt-6">
              <Heading level={3}>Don't have an account?</Heading>
              <button
                type="button"
                onClick={() => switchMode('register')}
                className="mt-2 text-2xl text-stone-900 font-bold underline underline-offset-2 hover:text-amber-700"
              >
                Register Now
              </button>
            </div>
          ) : (
            <p className="text-center text-sm text-stone-500 mt-6">
              {isForgot ? (
                <>
                  Remembered it?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="text-stone-900 font-bold hover:underline"
                  >
                    Back to Sign In
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="text-stone-900 font-bold hover:underline"
                  >
                    Sign In
                  </button>
                </>
              )}
            </p>
          )}
        </form>
      )}
    </Modal>
  );
};
